import { ChevronLeftIcon, ChevronRightIcon, MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { useEffect, useState, useMemo } from 'react';
import { useGetDatesCalendarQuery } from '../../store/services/CalendarHoursService';
import { useGetSediVisibiliQuery } from '../../store/services/CalendarHoursDaysService';
import CalendarDialog, { AppointmentSede } from '../../components/main/CalendarDialog';

const AbbrSede: Record<string, string> = {
    milano: 'MI',
    bologna: 'BO',
    napoli: 'NA',
    bari: 'BA'
};


export default function HoursCalendar({ globalAppointments }: any) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDay, setSelectedDay] = useState<any>(null);
    const [showModal, setShowModal] = useState(false);
    const [appointments, setAppointments] = useState<AppointmentSede | any>({});

    const { data: sediVisibiliData, isLoading: sediLoading } = useGetSediVisibiliQuery('');

    const sediDefinizioni = useMemo(() => [
        { id: 0, nome: 'Milano', colore: 'bg-blue-500', coloreBordo: 'border-blue-300', chiave: 'milano' },
        { id: 1, nome: 'Bologna', colore: 'bg-green-500', coloreBordo: 'border-green-300', chiave: 'bologna' },
        { id: 2, nome: 'Napoli', colore: 'bg-yellow-500', coloreBordo: 'border-yellow-300', chiave: 'napoli' },
        { id: 3, nome: 'Bari', colore: 'bg-purple-500', coloreBordo: 'border-purple-300', chiave: 'bari' }
    ], []);

    const sedi = useMemo(() => {
        return sediDefinizioni.filter(sede => sediVisibiliData?.result?.[sede.chiave] === true);
    }, [sediDefinizioni, sediVisibiliData]);

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;

    const { data: calendarData, isLoading } =
        useGetDatesCalendarQuery({ anno: year, mese: month });

    /**
     * Costruiamo:
     * appointments["2025-10-18-milano"] = 1
     */
    useEffect(() => {
        if (!calendarData?.result) return;

        const newAppointments: AppointmentSede | any = {};

        calendarData.result.forEach((item: any) => {
            const dateStr = item.data;

            sedi.forEach(sede => {
                const limite =
                    item?.[sede.chiave]?.limiteAppuntamenti;

                // mostriamo 0, nascondiamo solo null/undefined
                if (limite !== null && limite !== undefined) {
                    const key = `${dateStr}-${sede.chiave}`;
                    newAppointments[key] = limite;
                }
            });
        });

        setAppointments(newAppointments);
    }, [calendarData, sedi]);

    const getDaysInMonth = (date: Date) =>
        new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

    const getFirstDayOfMonth = (date: Date) =>
        new Date(date.getFullYear(), date.getMonth(), 1).getDay();

    const isDatePassed = (day: number) => {
        const dateToCheck = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            day
        );
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return dateToCheck < today;
    };

    const handlePrevMonth = () =>
        setCurrentDate(
            new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
        );

    const handleNextMonth = () =>
        setCurrentDate(
            new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
        );

    const handleDayClick = (day: number) => {
        if (isDatePassed(day)) return;

        const dateStr = `${currentDate.getFullYear()}-${String(
            currentDate.getMonth() + 1
        ).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

        setSelectedDay({ day, dateStr });
        setShowModal(true);
    };

    const getSedeInfo = (sede: any, dateStr: string) => {
        const key = `${dateStr}-${sede.chiave}`;
        const valore = appointments[key];

        return typeof valore === 'number'
            ? { ...sede, valore }
            : null;
    };


    const renderCalendar = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDay = getFirstDayOfMonth(currentDate);
        const days = [];

        // Creiamo una mappa: calendarMap["2025-12-14"]["milano"] = {...}
        const calendarMap: Record<string, any> = {};
        calendarData?.result?.forEach((item: any) => {
            calendarMap[item.data] = item;
        });

        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="p-2" />);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

            const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
            const passed = isDatePassed(day);

            const dayData = calendarMap[dateStr];

            const sediValorizzate = sedi
                .map(sede => getSedeInfo(sede, dateStr))
                .filter(Boolean);

            // Verifica simultanei solo una volta
            const hasMattina = dayData && sedi.some(sede => dayData[sede.chiave]?.limiteSimultaneiMattina > 0);
            const hasPomeriggio = dayData && sedi.some(sede => dayData[sede.chiave]?.limiteSimultaneiPomeriggio > 0);

            days.push(
                <div
                    key={day}
                    onClick={() => !passed && handleDayClick(day)}
                    className={`relative min-h-[5.5rem] sm:min-h-[7rem] border border-gray-200 p-1 sm:p-2 transition
                    ${passed ? 'bg-gray-100 cursor-not-allowed opacity-50' : isToday ? 'bg-blue-50 hover:bg-blue-100 cursor-pointer' : 'bg-white hover:bg-gray-50 cursor-pointer'}`}
                >
                    {/* numero giorno */}
                    <div className={`text-xs sm:text-sm font-semibold mb-1 sm:mb-2 ${passed ? 'text-gray-400' : isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                        {day}
                    </div>

                    {/* icone simultanei */}
                    <div className="absolute top-1 right-1 flex gap-0.5 sm:gap-1">
                        {hasMattina && <SunIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />}
                        {hasPomeriggio && <MoonIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />}
                    </div>

                    {/* lista sedi */}
                    {sediValorizzate.length > 0 && (
                        <div className="space-y-0.5 sm:space-y-1 mt-2 sm:mt-4">
                            {sediValorizzate.map((sede: any) => (
                                <div
                                    key={sede.chiave}
                                    className={`text-[9px] sm:text-xs px-1 sm:px-2 py-0.5 sm:py-1 rounded font-semibold truncate ${sede.valore === 0 ? 'bg-gray-200 text-gray-600' : sede.colore + ' text-white'}`}
                                >
                                    <span className="hidden md:inline">{sede.nome}: </span>
                                    <span className="inline md:hidden">{AbbrSede[sede.chiave] || sede.nome.substring(0, 2).toUpperCase()}: </span>
                                    {sede.valore}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return days;
    };

    const selectedDayData =
        calendarData?.result?.find(
            (d: any) => d.data === selectedDay?.dateStr
        ) || {};


    return (
        <div>
            <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6 mb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                    <h1 className="text-xl sm:text-3xl font-bold text-gray-800">
                        {currentDate
                            .toLocaleDateString('it-IT', {
                                month: 'long',
                                year: 'numeric'
                            })
                            .toUpperCase()}
                    </h1>

                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                        <button
                            onClick={handlePrevMonth}
                            className="h-10 bg-[#064e3b] text-white px-2 rounded-lg flex-1 sm:flex-initial flex items-center justify-center"
                        >
                            <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>

                        <button
                            onClick={() => setCurrentDate(new Date())}
                            className="h-10 bg-[#064e3b] text-white px-3 rounded-lg text-sm sm:text-base flex-1 sm:flex-initial"
                        >
                            Oggi
                        </button>

                        <select
                            value={currentDate.getMonth()}
                            onChange={e =>
                                setCurrentDate(
                                    new Date(
                                        currentDate.getFullYear(),
                                        Number(e.target.value),
                                        1
                                    )
                                )
                            }
                            className="px-3 py-2 border rounded-lg bg-white text-sm sm:text-base flex-1 sm:flex-initial min-w-[100px]"
                        >
                            {[
                                'Gennaio',
                                'Febbraio',
                                'Marzo',
                                'Aprile',
                                'Maggio',
                                'Giugno',
                                'Luglio',
                                'Agosto',
                                'Settembre',
                                'Ottobre',
                                'Novembre',
                                'Dicembre'
                            ].map((mese, i) => (
                                <option key={i} value={i}>
                                    {mese}
                                </option>
                            ))}
                        </select>

                        <button
                            onClick={handleNextMonth}
                            className="h-10 bg-[#064e3b] text-white px-2 rounded-lg flex-1 sm:flex-initial flex items-center justify-center"
                        >
                            <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                    </div>
                </div>

                {(isLoading || sediLoading) && (
                    <div className="text-center text-gray-500 mb-4 text-xs sm:text-sm">
                        Caricamento dati calendario...
                    </div>
                )}

                <div className="grid grid-cols-7 border border-gray-200 rounded-lg overflow-hidden">
                    {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map(d => (
                        <div
                            key={d}
                            className="bg-gray-100 p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm"
                        >
                            {d}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7 border border-t-0 border-gray-200">
                    {renderCalendar()}
                </div>
            </div>

            {showModal && selectedDay && (
                <CalendarDialog
                    sedi={sedi}
                    currentDate={currentDate}
                    appointments={selectedDayData}
                    setShowModal={setShowModal}
                    selectedDay={selectedDay}
                    globalAppointments={globalAppointments}
                />
            )}
        </div>
    );
}
